// src/lib/phone.ts
// Phone-number normalization for the frontend. Mirrors
// supabase/functions/_shared/phone.ts — keep the logic in sync so that
// frontend-canonicalized numbers and edge-function-canonicalized numbers
// are identical (auth emails, WhatsApp member matching, Whapi chat IDs).
//
// Canonical format: digits-only international — "4915156789012", "15551234567".

// Strip whitespace, "+", dashes (incl. unicode dashes), dots, slashes and
// parentheses — digits only. A parenthesized trunk marker like "+49 (0)151..."
// is removed first so the national "0" doesn't get baked into the digits.
export function normalizePhoneDigits(phone: string): string {
  return phone.replace(/\(0+\)/g, '').replace(/[\s+\-()./–—‐‑‒―]/g, '').trim();
}

// Country dial code → national number length (without leading 0).
export const COUNTRY_DIAL_CODES: Record<string, number> = {
  '49': 10,  // Germany
  '31': 9,   // Netherlands
  '43': 10,  // Austria
  '41': 9,   // Switzerland
  '44': 10,  // UK
  '33': 9,   // France
  '39': 10,  // Italy
  '34': 9,   // Spain
  '1': 10,   // US/Canada
  '352': 8,  // Luxembourg
  '32': 9,   // Belgium
  '45': 8,   // Denmark
  '46': 9,   // Sweden
  '47': 8,   // Norway
  '358': 9,  // Finland
  '48': 9,   // Poland
  '420': 9,  // Czech Republic
};

// Convert any phone format to canonical digits-only international form.
// Handles: +CC..., 00CC..., 0NN... (national), CC..., bare national.
// Bare numbers default to Germany unless they already start with a known
// country code of plausible length. A bare number starting with "1" is
// assumed to be a German mobile (0151/0160/0176…) without its leading 0 —
// US numbers are only detected with an explicit +1 or 001 prefix.
export function toCanonicalPhone(phone: string, defaultCountry = '49'): string | null {
  let clean = normalizePhoneDigits(phone);
  if (!clean) return null;

  if (clean.startsWith('+')) clean = clean.substring(1);

  if (clean.startsWith('00')) {
    const rest = clean.substring(2);
    for (const cc of Object.keys(COUNTRY_DIAL_CODES)) {
      if (rest.startsWith(cc)) return rest;
    }
    return rest;
  }

  const hadIntlPrefix =
    phone.trim().startsWith('+') || normalizePhoneDigits(phone).startsWith('00');
  const codes = Object.keys(COUNTRY_DIAL_CODES).sort((a, b) => b.length - a.length);
  for (const cc of codes) {
    if (clean.startsWith(cc)) {
      if (cc.length === 1 && !hadIntlPrefix) continue;
      const national = clean.substring(cc.length);
      const expectedLen = COUNTRY_DIAL_CODES[cc];
      const tolerance = cc.length >= 2 ? 2 : 0;
      if (
        national.length >= expectedLen - tolerance &&
        national.length <= expectedLen + tolerance
      ) {
        return clean;
      }
    }
  }

  if (clean.startsWith('0')) {
    return defaultCountry + clean.substring(1);
  }

  if (/^\d{8,11}$/.test(clean)) {
    // If the user explicitly wrote + or 00 but the country code is unknown
    // (e.g. +61 Australia), trust the explicit prefix instead of guessing
    // the default country.
    return hadIntlPrefix ? clean : defaultCountry + clean;
  }

  return clean;
}

// All common format variations for matching against stored member_phone
// values (which may be canonical digits, +CC, 00CC, 0-national or bare).
export function phoneVariations(phone: string): string[] {
  const canonical = toCanonicalPhone(phone);
  if (!canonical) return [];

  const variants = new Set<string>([canonical]);
  const codes = Object.keys(COUNTRY_DIAL_CODES).sort((a, b) => b.length - a.length);
  for (const cc of codes) {
    if (canonical.startsWith(cc)) {
      const national = canonical.substring(cc.length);
      variants.add(canonical);
      variants.add('+' + canonical);
      variants.add('0' + national);
      variants.add(national);
      variants.add('00' + canonical);
      break;
    }
  }
  return [...variants];
}

// E.164-style display/send format: "+4915156789012". Returns null if the
// input can't be normalized to a digits-only international number.
export function toE164(phone: string, defaultCountry = '49'): string | null {
  const canonical = toCanonicalPhone(phone, defaultCountry);
  if (!canonical || !/^\d+$/.test(canonical)) return null;
  return '+' + canonical;
}
